/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/entryAiFill.ts
 * FILE_ID: UI-KANBAN-ENTRIES-AIFILL-001
 * PURPOSE: Pure transforms for the Entries table's "AI draft rows"
 *          affordance — build the context text sent to the reasoner,
 *          and map its suggestions into editable draft rows.
 * WHY: Operator 2026-07-06: the table (ARC-3) and the AI ✓/✗ loop
 *      (ARC-7) both shipped, but nothing let AI SEED the table with
 *      standard sequenced rows ("painter is going to do this, then
 *      this") from the card's existing context. Rows land as DRAFTS —
 *      the operator edits/removes (✗) then "Save as sub-cards" (✓);
 *      the AI never mints cards on its own.
 * FLOW:
 *   card + live entries → buildEntriesAiContext → api.suggestCardActions
 *     → suggestionsToDrafts → EntryDraftRow[] (pre-filled, unsaved)
 * RELATED:
 *   - useEntriesAiFill.ts: the stateful hook calling these
 *   - email_kanban/services/card_suggest.py: the reasoner endpoint
 */

import type { KanbanMember } from '../../../api'
import type { CardLiveSuggestion } from '../../../api/payloads-cards'
import type { Card } from '../../../types'
import { emptyDraftRow, type EntryDraftRow } from './entryDraft'

/** Server-side floor (card_suggest.MIN_DRAFT_CHARS). Mirrored here so we
 *  can tell the operator "add a description first" BEFORE burning the
 *  per-card cooldown on a guaranteed `too_short` response. */
export const MIN_CONTEXT_CHARS = 80

/**
 * Compose the reasoner input from what the card already knows. The
 * endpoint treats `text` as an operator note, so we phrase the context
 * as one: title + description + existing entry titles (so the model
 * skips work that's already tabled).
 */
export function buildEntriesAiContext(card: Card, entries: Card[]): string {
  const parts: string[] = []
  if (card.title) parts.push(`Job/card: ${card.title}.`)
  const summary = (card.summary ?? '').trim()
  if (summary) parts.push(summary)
  if (entries.length > 0) {
    const existing = entries
      .map((e) => e.title)
      .filter(Boolean)
      .slice(0, 12)
      .join('; ')
    if (existing) parts.push(`Already tabled (do not repeat): ${existing}.`)
  }
  parts.push(
    'Break the remaining work into the standard sequenced steps for this '
      + 'job (who does what next, trade by trade), each as its own sub_card.',
  )
  return parts.join('\n')
}

/** True when the card carries too little context for the reasoner's
 *  min-chars guard — the caller should hint instead of calling. */
export function contextTooShort(text: string): boolean {
  return text.trim().length < MIN_CONTEXT_CHARS
}

/**
 * Reasoner suggestions → pre-filled draft rows. Every kind maps (a
 * checklist-ish `action` is still a valid table row); `detail` lands in
 * the "what/thing" cell, action_date (else due_date) in the date cell.
 * Teammate matches by name inside the title/detail are resolved when
 * unambiguous.
 */
export function suggestionsToDrafts(
  suggestions: CardLiveSuggestion[],
  members: KanbanMember[] = [],
): EntryDraftRow[] {
  const rows: EntryDraftRow[] = []
  for (const s of suggestions) {
    const title = (s.title ?? '').trim()
    if (!title) continue
    const row = emptyDraftRow()
    row.action = title.slice(0, 200)
    row.what = (s.detail ?? '').trim().slice(0, 200)
    const date = s.action_date ?? s.due_date
    row.date = /^\d{4}-\d{2}-\d{2}$/.test(date ?? '') ? (date as string) : ''
    const haystack = `${title} ${s.detail ?? ''}`.toLowerCase()
    const hits = members.filter(
      (m) => m.name && haystack.includes(m.name.trim().toLowerCase()),
    )
    if (hits.length === 1) row.teammate_id = hits[0].id
    rows.push(row)
  }
  return rows
}
